/*
 * This file is part of the Norges Musikkhøyskole Nettsted 2020 application.
 *
 * (c) APT AS
 *
 * For the full copyright and license information, please view the LICENSE
 * file that was distributed with this source code.
 */

import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import classNames from 'classnames';
import { parseHTML, decodeBase64 } from 'utils/DOM';

import styles from './Freeform.module.scss';

/**
 * This is the Freeform component.
 *
 * @author Anders Stalheim Øfsdahl <anders@apt.no>
 *
 * NOTE: This wrapper re-implements Freeform's AJAX submit, error/success UX,
 * and (post-Freeform v5 upgrade) reCAPTCHA bootstrap. Freeform v5 now handles
 * all of this natively via `data-freeform` attributes + freeform.js. The
 * wrapper is fighting the plugin rather than leveraging it, which is why this
 * file contains workarounds for:
 *   - script tags inside form.render() output not executing (we re-create them)
 *   - reCAPTCHA auto-render failing silently (we render it manually)
 *   - duplicate freeform.js loads attaching multiple submit listeners on the
 *     same form, causing the captcha token to be replayed (we guard with
 *     submittingRef)
 *
 * TODO: Refactor to drop the React wrapper and use Freeform 5 natively —
 * render `{{ form.render() }}` server-side and attach small adapters to
 * Freeform's `freeform-ajax-success` / `freeform-ajax-error` events for the
 * design-system overlay styling. That removes all the workarounds below and
 * stops the migration tax we pay on every Freeform upgrade.
 *
 * @return {JSX}
 */
function Freeform({ encodedForm, labels, onSuccessClose, children }) {
  const refWrapper = useRef();
  const refForm = useRef();
  const submittingRef = useRef(false);

  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [success, setSuccess] = useState(false);
  const [finished, setFinished] = useState(false);
  const [errors, setErrors] = useState(null);
  const [formErrors, setFormErrors] = useState(null);
  const [hasRequiredFields, setHasRequiredFields] = useState(false);

  // https://docs.solspace.com/craft/freeform/v3/templating/ajax-forms.html#manual-implementations
  const ajaxifyForm = (event) => {
    event.stopPropagation();
    event.preventDefault();

    // Multiple freeform.js copies on the page attach duplicate
    // freeform-on-submit listeners — guard against re-entry so the reCAPTCHA
    // token isn't reused (Google rejects token reuse).
    if (submittingRef.current) return;
    submittingRef.current = true;

    const data = new FormData(refForm.current);

    const method = refForm.current.getAttribute('method');
    const action =
      refForm.current.getAttribute('action') ??
      refForm.current.querySelector('input[name=action]').value ??
      false;

    if (!action) {
      console.warn('Unable to determine action of form');
      return;
    }

    setSubmitting(true);
    setSubmitted(false);

    // Clear previous error messages, error classes etc, callouts
    setSuccess(null);
    setFinished(null);
    setFormErrors(null);
    setErrors(null);

    const callout = document.querySelector('.form__callout');
    if (callout) {
      callout.parentNode.removeChild(callout);
    }

    const uls = document.querySelectorAll('.form__errors');
    if (uls) {
      uls.forEach((ul) => {
        ul.parentNode.removeChild(ul);
      });
    }

    const labels = document.querySelectorAll('.form__label-has-errors');
    if (labels) {
      labels.forEach((label) =>
        label.classList.remove('form__label-has-errors')
      );
    }

    axios({
      url: action ? action : window.location.href,
      method: method ? method : 'post',
      data: data,
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        HTTP_X_REQUESTED_WITH: 'XMLHttpRequest',
      },
    })
      .then((responseObject) => {
        let response = responseObject.data;

        setSubmitting(false);
        setSubmitted(true);

        setSuccess(response.success);
        setFinished(response.finished);
        setFormErrors(response.formErrors);
        setErrors(response.errors);
      })
      .catch((error) => {
        setSubmitting(false);
        setFormErrors([error]);
      })
      .finally(() => {
        submittingRef.current = false;
      });
  };

  // Dirty dirty, since we're rendering the form as a document fragment
  // Have to iterate fields and use querySelector to find and add classes
  const addErrorToFields = () => {
    if (submitted && !success && errors) {
      Object.keys(errors).forEach((key) => {
        const error = errors[key];

        // Find input
        const input = refForm.current.querySelector(
          `#form-input-${key}, input[name="${key}"]`
        );
        if (!input) {
          return;
        }

        // Find parent colum node
        const column = input.closest('.form__column');
        if (!column) {
          return;
        }

        // Find label
        const label = column.querySelector(`label[for="form-input-${key}"]`);
        if (!label) {
          return;
        }

        // Add error class to label
        label.classList.add('form__label-has-errors');

        // Add list of errors to column
        const err = document.createElement('ul');
        err.classList.add('errors', 'form__errors');
        error.forEach((e) => {
          const li = document.createElement('li');
          li.innerHTML = e;
          err.appendChild(li);
        });

        column.appendChild(err);
      });
    }
  };

  const insertForm = (ref) => {
    if (!refWrapper.current) {
      refWrapper.current = ref;

      const decodedForm = decodeBase64(encodedForm);

      // Append children OR form prop
      const fragment = decodedForm ? parseHTML(decodedForm) : children;

      // Append the full fragment (form + sibling <script>/<link> tags emitted
      // after </form> by Freeform v5 for the captcha bootstrap).
      refWrapper.current.appendChild(fragment);

      // Scripts inserted via innerHTML/template do not execute. Re-create them
      // so Freeform's loaders (freeform.js, captcha scripts) actually run.
      refWrapper.current.querySelectorAll('script').forEach((oldScript) => {
        const newScript = document.createElement('script');
        for (const attr of oldScript.attributes) {
          newScript.setAttribute(attr.name, attr.value);
        }
        if (oldScript.textContent) {
          newScript.textContent = oldScript.textContent;
        }
        oldScript.parentNode.replaceChild(newScript, oldScript);
      });

      // Freeform v5's auto-render of reCAPTCHA races with the double-loaded
      // v2-checkbox.js (inline + page-level) — `grecaptcha.ready()` fires
      // before grecaptcha is defined, the throw is swallowed by Freeform's
      // .catch(()=>{}), and the widget never renders. Render it ourselves.
      const captchaContainer = refWrapper.current.querySelector(
        '[data-captcha="recaptcha"]'
      );
      if (captchaContainer) {
        let attempts = 0;
        const MAX_ATTEMPTS = 50; // ~5s at 100ms intervals
        const renderCaptcha = () => {
          if (
            typeof window.grecaptcha === 'undefined' ||
            typeof window.grecaptcha.render !== 'function'
          ) {
            if (attempts++ >= MAX_ATTEMPTS) {
              console.warn(
                '[Freeform] grecaptcha never loaded — captcha not rendered'
              );
              return;
            }
            setTimeout(renderCaptcha, 100);
            return;
          }
          let target = captchaContainer.querySelector('.g-recaptcha');
          if (!target) {
            target = document.createElement('div');
            target.classList.add('g-recaptcha');
            captchaContainer.appendChild(target);
          }
          if (target.dataset.captchaId) return;
          window.grecaptcha.ready(() => {
            try {
              const id = window.grecaptcha.render(target, {
                sitekey: captchaContainer.dataset.sitekey,
                theme: captchaContainer.dataset.theme || 'light',
                size: captchaContainer.dataset.size || 'normal',
              });
              target.dataset.captchaId = String(id);
            } catch (e) {
              console.warn('[Freeform] reCAPTCHA render failed:', e);
            }
          });
        };
        renderCaptcha();
      }

      // Attach event listener
      refForm.current = refWrapper.current.querySelector('form');
      if (refForm.current) {
        refForm.current.addEventListener('freeform-on-submit', ajaxifyForm);

        refForm.current.addEventListener(
          'freeform-render-form-errors',
          function (event) {
            // Stop the default form error rendering
            event.preventDefault();
          }
        );

        refForm.current.addEventListener(
          'freeform-render-field-errors',
          function (event) {
            // Stop the default form error rendering
            event.preventDefault();
          }
        );

        refForm.current.addEventListener(
          'freeform-ajax-success',
          function (event) {
            event.preventDefault();
          }
        );

        // Check if form has required fields
        const requiredFields = refForm.current.querySelectorAll(
          '[required], .required, [data-required]'
        );

        if (requiredFields.length > 0) {
          setHasRequiredFields(true);

          // Iterate required field set required attribute to true
          requiredFields.forEach((input) => {
            if (input) {
              input.setAttribute('required', true);
            }
          });
        }
      }
    }
  };

  const renderSuccess = () => {
    return (
      <div className={classNames(styles.content, styles.contentSuccess)}>
        <p>{labels.success}</p>
        <button
          onClick={() => {
            onSuccessClose();
            setSubmitting(false);
            setSubmitted(false);
            refForm.current.reset();
          }}
          className={styles.close}
          aria-label="Close"
        />
      </div>
    );
  };

  const renderError = () => {
    return (
      <div className={classNames(styles.content, styles.contentError)}>
        <p>{labels.error}</p>
        {formErrors && formErrors.length > 0 && (
          <ul>
            {formErrors.map((error) => (
              <li key={error}>{error}</li>
            ))}
          </ul>
        )}
        <button
          onClick={() => {
            addErrorToFields();
            setSubmitting(false);
            setSubmitted(false);
            // scroll into view
            refForm.current.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            });
          }}
          className={styles.close}
          aria-label="Close"
        />
      </div>
    );
  };

  if (!children && !encodedForm) {
    return null;
  }

  return (
    <div
      className={classNames(styles.main, { [styles.submitting]: submitting })}
    >
      {hasRequiredFields && (
        <p className="form__required-fields">{labels.requiredFields}</p>
      )}

      <div ref={insertForm}></div>

      <div
        className={classNames(styles.overlay, {
          [styles.overlayVisible]: submitting || submitted,
        })}
      >
        {submitted && !success && renderError()}
        {submitted && success && renderSuccess()}
      </div>
    </div>
  );
}

/**
 * Declare expected prop types.
 *
 * @type {Object}
 */
Freeform.propTypes = {
  onSuccessClose: PropTypes.func.isRequired,
  encodedForm: PropTypes.string,
  labels: PropTypes.shape({
    success: PropTypes.string,
    error: PropTypes.string,
    requiredFields: PropTypes.string,
  }),
};

/**
 * Declare defaults for non-required props.
 *
 * @type {Object}
 */
Freeform.defaultProps = {
  onSuccessClose: () => {},
  encodedForm: null,
  labels: {
    success: 'Yay!',
    error: 'Nay!',
    requiredFields: 'Req!',
  },
};

export default Freeform;
